import React, { useState, useRef, useCallback } from 'react';
import { uploadApi, imagesApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface AddImageButtonProps {
  onImageAdded?: () => void;
  className?: string;
}

interface UploadedImage {
  file: File;
  url: string;
  filename: string;
  originalDimensions: { width: number; height: number };
}

export const AddImageButton: React.FC<AddImageButtonProps> = ({ 
  onImageAdded, 
  className = '' 
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [showModal, setShowModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<UploadedImage | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [cropArea, setCropArea] = useState({
    x: 0,
    y: 0,
    width: 100,
    height: 100
  });
  

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Create preview URL
    const url = URL.createObjectURL(file);
    
    // Get image dimensions
    const img = new Image();
    img.onload = () => {
      const uploadedImg: UploadedImage = {
        file,
        url,
        filename: file.name,
        originalDimensions: { width: img.width, height: img.height }
      };
      
        // Set initial crop area with 4:3 aspect ratio
        const targetRatio = 4 / 3; // 1.333
      const imgRatio = img.width / img.height;
      
      let cropWidth, cropHeight;
      
      if (imgRatio > targetRatio) {
        // Image is wider - fit to height
        cropHeight = img.height * 0.8;
        cropWidth = cropHeight * targetRatio;
      } else {
        // Image is taller - fit to width
        cropWidth = img.width * 0.8;
        cropHeight = cropWidth / targetRatio;
      }
      
      // Center the crop area
      const centerX = (img.width - cropWidth) / 2;
      const centerY = (img.height - cropHeight) / 2;
      
      setCropArea({
        x: centerX,
        y: centerY,
        width: cropWidth,
        height: cropHeight
      });
      
      setUploadedImage(uploadedImg);
      setShowModal(true);
    };
    img.src = url;
  }, []);

  const resetModal = () => {
    setShowModal(false);
    setUploadedImage(null);
    setIsUploading(false);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadAndProcess = async () => {
    if (!uploadedImage) return;

    try {
      // Upload the image
    setIsUploading(true);
      const formData = new FormData();
      formData.append('images', uploadedImage.file);

      const uploadResponse = await uploadApi.uploadImages(formData);
      if (!uploadResponse.data.success || !uploadResponse.data.data?.files?.length) {
        throw new Error('Upload failed');
      }

      const uploadedFile = uploadResponse.data.data.files[0];
      let processedFilename = uploadedFile.filename;

      // Always crop to LinkedIn/Facebook dimensions (1200x630)
      setIsProcessing(true);
      
      // Ensure crop values are valid integers
      const validCropArea = {
        x: Math.round(Math.max(0, cropArea.x)),
        y: Math.round(Math.max(0, cropArea.y)),
        width: Math.round(Math.max(50, cropArea.width)),
        height: Math.round(Math.max(50, cropArea.height))
      };
      
      console.log('Crop area:', validCropArea);
      
      const cropResponse = await imagesApi.crop(uploadedFile.filename, validCropArea);
      
      if (cropResponse.data.success && cropResponse.data.data?.filename) {
        // processedFilename = cropResponse.data.data.filename; // Currently unused
      }

      // Photo record is automatically created during the crop operation
      console.log('Photo uploaded and processed successfully');

      // Success - close modal and refresh
      console.log('Upload completed successfully, closing modal and calling onImageAdded');
      setShowModal(false);
      if (onImageAdded) {
        onImageAdded();
      }

    } catch (error: any) {
      console.error('Failed to process image:', error);
      alert(`Failed to process image: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <button
        onClick={() => fileInputRef.current?.click()}
        className={`btn-primary p-2 ${className}`}
        title="Add Image"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Processing Modal */}
      {showModal && uploadedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full h-full max-w-5xl max-h-[90vh] flex flex-col">
              {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Crop Image
                </h2>
                <button
                  onClick={resetModal}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

            {/* Image Area */}
            <div className="flex-1 p-6 min-h-0">
              <div className="relative w-full h-full bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                    <img
                      src={uploadedImage.url}
                      alt="Upload preview"
                  className="w-full h-full object-contain"
                      id="crop-image"
                    />
                    
                {/* Draggable Crop Frame */}
                    <div 
                      className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 cursor-move"
                      style={{
                        left: `${4 + (cropArea.x / uploadedImage.originalDimensions.width) * 92}%`,
                        top: `${4 + (cropArea.y / uploadedImage.originalDimensions.height) * 92}%`,
                        width: `${(cropArea.width / uploadedImage.originalDimensions.width) * 92}%`,
                        height: `${(cropArea.height / uploadedImage.originalDimensions.height) * 92}%`
                      }}
                      onMouseDown={(e) => {
                    // Only handle drag if not clicking on resize handles
                    if ((e.target as HTMLElement).classList.contains('resize-handle')) return;
                    
                        e.preventDefault();
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startCropX = cropArea.x;
                        const startCropY = cropArea.y;
                        
                        const handleMouseMove = (e: MouseEvent) => {
                          const deltaX = e.clientX - startX;
                          const deltaY = e.clientY - startY;
                          const img = document.getElementById('crop-image') as HTMLImageElement;
                          if (!img) return;
                          
                          const rect = img.getBoundingClientRect();
                          const scaleX = uploadedImage.originalDimensions.width / rect.width;
                          const scaleY = uploadedImage.originalDimensions.height / rect.height;
                          
                          const newX = Math.max(0, Math.min(
                            uploadedImage.originalDimensions.width - cropArea.width,
                            startCropX + (deltaX * scaleX)
                          ));
                          const newY = Math.max(0, Math.min(
                            uploadedImage.originalDimensions.height - cropArea.height,
                            startCropY + (deltaY * scaleY)
                          ));
                          
                          setCropArea(prev => ({ ...prev, x: newX, y: newY }));
                        };
                        
                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };
                        
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                    >
                  {/* Resizable Corner handles */}
                  <div 
                    className="resize-handle absolute -top-1 -left-1 w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-nw-resize"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startCrop = { ...cropArea };
                      
                      const handleMouseMove = (e: MouseEvent) => {
                        const deltaX = e.clientX - startX;
                        const deltaY = e.clientY - startY;
                        const img = document.getElementById('crop-image') as HTMLImageElement;
                        if (!img) return;
                        
                        const rect = img.getBoundingClientRect();
                        const scaleX = uploadedImage.originalDimensions.width / rect.width;
                        const scaleY = uploadedImage.originalDimensions.height / rect.height;
                        
                        const deltaXScaled = deltaX * scaleX;
                        const deltaYScaled = deltaY * scaleY;
                        
                        // Maintain 4:3 aspect ratio
                        const targetRatio = 4 / 3;
                        
                        // Use the smaller delta to maintain ratio
                        const newWidth = Math.max(50, startCrop.width - deltaXScaled);
                        const newHeight = newWidth / targetRatio;
                        
                        const newX = Math.max(0, startCrop.x + deltaXScaled);
                        const newY = Math.max(0, startCrop.y + deltaYScaled);
                        
                        setCropArea({
                          x: newX,
                          y: newY,
                          width: Math.min(newWidth, uploadedImage.originalDimensions.width - newX),
                          height: Math.min(newHeight, uploadedImage.originalDimensions.height - newY)
                        });
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  ></div>
                  
                  <div 
                    className="resize-handle absolute -top-1 -right-1 w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-ne-resize"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startCrop = { ...cropArea };
                      
                      const handleMouseMove = (e: MouseEvent) => {
                        const deltaX = e.clientX - startX;
                        const deltaY = e.clientY - startY;
                        const img = document.getElementById('crop-image') as HTMLImageElement;
                        if (!img) return;
                        
                        const rect = img.getBoundingClientRect();
                        const scaleX = uploadedImage.originalDimensions.width / rect.width;
                        const scaleY = uploadedImage.originalDimensions.height / rect.height;
                        
                        const deltaXScaled = deltaX * scaleX;
                        const deltaYScaled = deltaY * scaleY;
                        
                        // Maintain 4:3 aspect ratio
                        const targetRatio = 4 / 3;
                        
                        const newWidth = Math.max(50, startCrop.width + deltaXScaled);
                        const newHeight = newWidth / targetRatio;
                        const newY = Math.max(0, startCrop.y + deltaYScaled);
                        
                        setCropArea({
                          x: startCrop.x,
                          y: newY,
                          width: Math.min(newWidth, uploadedImage.originalDimensions.width - startCrop.x),
                          height: Math.min(newHeight, uploadedImage.originalDimensions.height - newY)
                        });
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  ></div>
                  
                  <div 
                    className="resize-handle absolute -bottom-1 -left-1 w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-sw-resize"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startCrop = { ...cropArea };
                      
                      const handleMouseMove = (e: MouseEvent) => {
                        const deltaX = e.clientX - startX;
                        const deltaY = e.clientY - startY;
                        const img = document.getElementById('crop-image') as HTMLImageElement;
                        if (!img) return;
                        
                        const rect = img.getBoundingClientRect();
                        const scaleX = uploadedImage.originalDimensions.width / rect.width;
                        const scaleY = uploadedImage.originalDimensions.height / rect.height;
                        
                        const deltaXScaled = deltaX * scaleX;
                        const deltaYScaled = deltaY * scaleY;
                        
                        // Maintain 4:3 aspect ratio
                        const targetRatio = 4 / 3;
                        
                        const newWidth = Math.max(50, startCrop.width - deltaXScaled);
                        const newHeight = newWidth / targetRatio;
                        const newX = Math.max(0, startCrop.x + deltaXScaled);
                        
                        setCropArea({
                          x: newX,
                          y: startCrop.y,
                          width: Math.min(newWidth, uploadedImage.originalDimensions.width - newX),
                          height: Math.min(newHeight, uploadedImage.originalDimensions.height - startCrop.y)
                        });
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  ></div>
                  
                  <div 
                    className="resize-handle absolute -bottom-1 -right-1 w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-se-resize"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startCrop = { ...cropArea };
                      
                      const handleMouseMove = (e: MouseEvent) => {
                        const deltaX = e.clientX - startX;
                        const deltaY = e.clientY - startY;
                        const img = document.getElementById('crop-image') as HTMLImageElement;
                        if (!img) return;
                        
                        const rect = img.getBoundingClientRect();
                        const scaleX = uploadedImage.originalDimensions.width / rect.width;
                        const scaleY = uploadedImage.originalDimensions.height / rect.height;
                        
                        const deltaXScaled = deltaX * scaleX;
                        const deltaYScaled = deltaY * scaleY;
                        
                        // Maintain 4:3 aspect ratio
                        const targetRatio = 4 / 3;
                        
                        const newWidth = Math.max(50, startCrop.width + deltaXScaled);
                        const newHeight = newWidth / targetRatio;
                        
                        setCropArea({
                          x: startCrop.x,
                          y: startCrop.y,
                          width: Math.min(newWidth, uploadedImage.originalDimensions.width - startCrop.x),
                          height: Math.min(newHeight, uploadedImage.originalDimensions.height - startCrop.y)
                        });
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  ></div>
                    </div>
                  </div>
                </div>

            {/* Bottom Section */}
            <div className="p-6 border-t border-slate-200 dark:border-slate-700">

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Drag to move • Drag corners to resize • Final size: 1200×630px
                </p>
                <div className="flex space-x-3">
                <button
                  onClick={resetModal}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadAndProcess}
                  disabled={isUploading || isProcessing}
                  className="btn-primary flex items-center space-x-2"
                >
                  {(isUploading || isProcessing) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>
                        {isUploading ? 'Uploading...' : 'Processing...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Upload Photo</span>
                    </>
                  )}
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
